/**
 * <cart-upsell>
 *
 * Cart drawer upsell filled from Shopify Search & Discovery recommendations.
 * - layout "slider" (items in cart): recommendations for every product in the cart, merged,
 *   de-duplicated and without the products that are already in the cart.
 * - layout "grid" (empty cart): server rendered fallback collection, replaced by recommendations
 *   for the current product when there are any.
 *
 * The theme re-renders the cart upsell holder (api-cart-items section) on every cart change, so a
 * fresh element connects each time. Responses are cached per product, which lets a quantity
 * change re-render from memory instead of hitting the network again.
 */
(() => {
  if (customElements.get('cart-upsell')) return;

  // Recommendations for a single product never change during a visit.
  const responseCache = new Map();

  // Every product in the cart costs a request; the first few are enough to fill 8 slots.
  const MAX_SOURCE_PRODUCTS = 4;

  // The recommendations endpoint caps `limit` at 10.
  const REQUEST_LIMIT = 10;

  class CartUpsell extends HTMLElement {
    #abortController = null;
    #resizeObserver = null;
    #progressFrame = 0;
    #trackWidth = 0;
    #viewportWidth = 0;

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.list = this.querySelector('[data-cart-upsell-list]');
      this.nav = this.querySelector('[data-cart-upsell-nav]');
      this.progressBar = this.querySelector('[data-cart-upsell-progress]');
      this.prevButton = this.querySelector('[data-cart-upsell-prev]');
      this.nextButton = this.querySelector('[data-cart-upsell-next]');
      this.productIds = (this.dataset.productIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (this.#isSlider) {
        this.prevButton.addEventListener('click', this.#onPrevClick);
        this.nextButton.addEventListener('click', this.#onNextClick);
        this.list.addEventListener('scroll', this.#onListScroll, {passive: true});
        this.#resizeObserver = new ResizeObserver(() => this.#updateNav());
        this.#resizeObserver.observe(this.list);
      }

      this.#load();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#resizeObserver?.disconnect();
      cancelAnimationFrame(this.#progressFrame);

      if (this.#isSlider) {
        this.prevButton.removeEventListener('click', this.#onPrevClick);
        this.nextButton.removeEventListener('click', this.#onNextClick);
        this.list.removeEventListener('scroll', this.#onListScroll);
      }

      this.initialized = false;
    }

    get #isSlider() {
      return this.dataset.layout === 'slider';
    }

    get #sourceIds() {
      return this.productIds.slice(0, MAX_SOURCE_PRODUCTS);
    }

    #cacheKey(productId) {
      return `${this.dataset.sectionId}:${this.dataset.intent}:${productId}`;
    }

    async #load() {
      const sourceIds = this.#sourceIds;
      if (!sourceIds.length) return;

      // Everything cached: render synchronously so the slider does not blink on cart updates.
      if (sourceIds.every((id) => responseCache.has(this.#cacheKey(id)))) {
        this.#render(sourceIds.map((id) => responseCache.get(this.#cacheKey(id))));
        return;
      }

      this.#abortController?.abort();
      this.#abortController = new AbortController();
      const {signal} = this.#abortController;

      try {
        const results = await Promise.all(sourceIds.map((id) => this.#fetchRecommendations(id, signal)));
        this.#render(results);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (this.#isSlider) this.hidden = true;
      }
    }

    async #fetchRecommendations(productId, signal) {
      const key = this.#cacheKey(productId);
      if (responseCache.has(key)) return responseCache.get(key);

      const intent = this.dataset.intent || 'complementary';
      let items = await this.#requestItems(productId, intent, signal);

      // Complementary products only exist where they were set up in Search & Discovery.
      if (!items.length && intent === 'complementary') {
        items = await this.#requestItems(productId, 'related', signal);
      }

      responseCache.set(key, items);
      return items;
    }

    async #requestItems(productId, intent, signal) {
      const params = new URLSearchParams({
        product_id: productId,
        limit: REQUEST_LIMIT,
        intent,
        section_id: this.dataset.sectionId,
      });

      const response = await fetch(`${this.dataset.url}?${params}`, {signal});
      if (!response.ok) throw new Error(`Cart upsell recommendations failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return [...html.querySelectorAll('[data-cart-upsell-item]')].map((item) => ({
        id: item.dataset.productId,
        html: item.outerHTML,
      }));
    }

    /**
     * Merges the per-product lists round-robin, so every cart product contributes its best matches
     * before any of them fills the remaining slots.
     */
    #render(results) {
      const limit = Number(this.dataset.limit) || 8;
      const excluded = new Set(this.productIds);
      const seen = new Set();
      const merged = [];
      const longest = Math.max(0, ...results.map((items) => items.length));

      for (let index = 0; index < longest && merged.length < limit; index++) {
        for (const items of results) {
          const item = items[index];
          if (!item || excluded.has(item.id) || seen.has(item.id)) continue;
          seen.add(item.id);
          merged.push(item.html);
          if (merged.length === limit) break;
        }
      }

      // Nothing recommended: the empty cart keeps its fallback collection, the slider disappears.
      if (!merged.length) {
        if (this.#isSlider || !this.list.children.length) this.hidden = true;
        return;
      }

      this.list.innerHTML = merged.join('');
      this.hidden = false;

      if (this.#isSlider) {
        this.list.scrollLeft = 0;
        this.#updateNav();
      }
    }

    #onPrevClick = () => this.#scrollByCard(-1);

    #onNextClick = () => this.#scrollByCard(1);

    #scrollByCard(direction) {
      const card = this.list.firstElementChild;
      if (!card) return;

      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.list.scrollBy({
        left: direction * (card.offsetWidth + gap),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    #onListScroll = () => {
      cancelAnimationFrame(this.#progressFrame);
      this.#progressFrame = requestAnimationFrame(() => this.#updateProgress());
    };

    /**
     * Measures the track once per resize or content change, so the scroll handler only has to
     * read scrollLeft.
     */
    #updateNav() {
      this.#trackWidth = this.list.scrollWidth;
      this.#viewportWidth = this.list.clientWidth;

      const overflows = this.#trackWidth > this.#viewportWidth + 1;
      this.nav.hidden = !overflows;
      if (overflows) this.#updateProgress();
    }

    #updateProgress() {
      const {scrollLeft} = this.list;
      const maxScroll = this.#trackWidth - this.#viewportWidth;
      const size = this.#viewportWidth / this.#trackWidth;
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;

      this.progressBar.style.setProperty('--cart-upsell-progress-size', size);
      this.progressBar.style.setProperty('--cart-upsell-progress-offset', progress * (1 / size - 1));

      this.prevButton.disabled = scrollLeft <= 1;
      this.nextButton.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('cart-upsell', CartUpsell);
})();
